import React from "react"
import { Col, Flex, Row, Tag, Typography } from "antd"
import { TeamOutlined, FieldTimeOutlined, FileTextOutlined, CheckCircleOutlined, StopOutlined } from "@ant-design/icons"
import { useWizardContext } from "../../../WizardContext"
import styles from "./PreviewTicketTypes.module.css"

const { Title, Text } = Typography

const DURATION_LABELS = {
  single_day: "Single Day",
  multi_day: "Multi-Day"
}

const getCapacityDisplay = (minTickets, maxTickets) => {
  if (minTickets != null && maxTickets != null) return `${minTickets}–${maxTickets} tickets`
  if (minTickets != null) return `Min ${minTickets} tickets`
  return `Max ${maxTickets} tickets`
}

export const PreviewTicketTypes = () => {
  const { form } = useWizardContext()
  const ticketTypes = form.getFieldValue("ticket_types") || []
  const ticketDuration = form.getFieldValue("ticket_duration")
  const minTickets = form.getFieldValue("min_tickets")
  const maxTickets = form.getFieldValue("max_tickets")
  const ticketDescription = form.getFieldValue("ticket_description")

  const hasData = ticketTypes.length > 0 || minTickets != null || maxTickets != null || ticketDescription || ticketDuration

  if (!hasData) return null

  const capacityDisplay = getCapacityDisplay(minTickets, maxTickets)

  return (
    <div className={styles.container}>
      <Title level={3} className={styles.title}>{"Tickets"}</Title>

      {(ticketDuration || minTickets != null || maxTickets != null) && (
        <Row gutter={[12, 12]} className={styles.metaRow}>
          {ticketDuration && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <FieldTimeOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Duration"}</Text>
                </Flex>
                <Text strong className={styles.metaValue}>{DURATION_LABELS[ticketDuration] ?? ticketDuration}</Text>
              </div>
            </Col>
          )}
          {(minTickets != null || maxTickets != null) && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <TeamOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Capacity"}</Text>
                </Flex>
                <Text strong className={styles.metaValue}>{capacityDisplay}</Text>
              </div>
            </Col>
          )}
        </Row>
      )}

      {ticketDescription && (
        <Flex align="flex-start" gap="small" className={styles.descriptionRow}>
          <FileTextOutlined className={styles.descIcon} />
          <Text className={styles.description}>{ticketDescription}</Text>
        </Flex>
      )}

      {ticketTypes.length > 0 && (
        <Flex vertical gap="small" className={styles.typesList}>
          {ticketTypes.filter((t) => t.enabled !== false).map((ticket) => (
            <div key={ticket.id} className={styles.ticketCard}>
              <Flex justify="space-between" align="center">
                <Flex align="center" gap="small">
                  <CheckCircleOutlined className={styles.activeIcon} />
                  <Text strong>{ticket.title}</Text>
                </Flex>
                <Tag color="success" className={styles.statusTag}>{"Active"}</Tag>
              </Flex>
              {ticket.description && (
                <Text className={styles.ticketDescription}>{ticket.description}</Text>
              )}
            </div>
          ))}
          {ticketTypes.filter((t) => t.enabled === false).map((ticket) => (
            <div key={ticket.id} className={`${styles.ticketCard} ${styles.ticketCardDisabled}`}>
              <Flex justify="space-between" align="center">
                <Flex align="center" gap="small">
                  <StopOutlined className={styles.inactiveIcon} />
                  <Text className={styles.ticketNameDisabled}>{ticket.title}</Text>
                </Flex>
                <Tag className={styles.statusTag}>{"Inactive"}</Tag>
              </Flex>
            </div>
          ))}
        </Flex>
      )}
    </div>
  )
}
